import { Component, OnDestroy, OnInit, ComponentRef } from '@angular/core';
import { SprintService } from '../../../services/sprint.service';
import { SettingsService } from '../../../services/settings.service';
import { Subscription, Observable } from 'rxjs';
import { Sprint } from '../../../models/sprint';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { CreateSprintErrorMessageComponent } from '../../create-sprint-error-message/create-sprint-error-message.component';
import { IWidget } from '../../../interfaces/iWidget';

export interface CreateSprintErrorMessageData {
  name: string;
  startDate: Date;
  endDate: Date;
}

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-sprintinfo-widget',
  templateUrl: './sprintinfo-widget.component.html',
  styleUrls: ['./sprintinfo-widget.component.scss'],
  providers: [{ provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
  { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }]
  })
export class SprintinfoWidgetComponent implements IWidget, OnInit, OnDestroy {
  static widgetName = 'sprintinfo';
  public sprint: Sprint = null;
  private currentSprintDuration: number;
  public currentSprintDurationAsDays: number;
  private updateEvent: Subscription;
  private readonly oneDayInMilliseconds = 24 * 60 * 60 * 1000;
  private projectName = this.settingsService.getProjectName();
  private editModeSprintName = false;
  private createSprintErrorMessageRef: MatDialogRef<CreateSprintErrorMessageComponent>;

  // callbacks
  public setPosition;

  // ref
  public componentRef: ComponentRef<any>;
  public isUndocked: boolean;

  constructor(
    private sprintService: SprintService,
    private settingsService: SettingsService,
    private createSprintErrorDialog: MatDialog
  ) { }

  ngOnInit() {
    this.updateData();
    this.updateEvent = this.settingsService.getUpdateObservable().subscribe(res => {
      this.updateData();
    });
  }

  ngOnDestroy(): void {
    this.updateEvent.unsubscribe();
  }

  public getName(): string {
    return SprintinfoWidgetComponent.widgetName;
  }

  public setUndocked(isUndocked: boolean): void {
    this.isUndocked = isUndocked;
  }

  updateData(): void {
    const projectKey = this.settingsService.getProjectName();
    const sprintId = this.settingsService.getSprintId(projectKey);

    if (projectKey && sprintId) {
      this.sprintService.getSprintDetails(projectKey, sprintId).subscribe(sprint => {
        this.sprint = sprint;
        const startDate = new Date(this.sprint.startDate);
        const endDate = new Date(this.sprint.endDate);
        this.currentSprintDuration = endDate.valueOf() - startDate.valueOf();
        this.currentSprintDurationAsDays = Math.round(this.currentSprintDuration / this.oneDayInMilliseconds);
      });
    }
  }

  updateSprintStartDate(event: any) {
    this.sprint.startDate = event.value.toISOString();
    const startDate = new Date(this.sprint.startDate);
    const endDate = new Date(this.sprint.endDate);
    this.currentSprintDuration = endDate.valueOf() - startDate.valueOf();
    this.currentSprintDurationAsDays = Math.round(this.currentSprintDuration / this.oneDayInMilliseconds);
  }

  updateSprintDuration(event: any): void {
    const obj = this;
    const startDate = new Date(obj.sprint.startDate);
    const endDate = obj.addDaysToDate(startDate, event.value);
    obj.sprint.endDate = endDate.toISOString();
    this.currentSprintDuration = endDate.valueOf() - startDate.valueOf();
    this.currentSprintDurationAsDays = Math.round(this.currentSprintDuration / this.oneDayInMilliseconds);
  }

  addDaysToDate(date: Date, days: number): Date {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() + days);
    return newDate;
  }

  updateSprintDate(event: any): void {
    const localThis = this;
    const startDate = new Date(this.sprint.startDate);
    const endDate = new Date(this.sprint.endDate);
    localThis.sprintService.getSprints(this.projectName).subscribe(sprints => {
      sprints = sprints.filter(sprint => sprint.id !== this.sprint.id);
      if (localThis.checkIfSprintsOverlap(startDate, endDate, sprints) == null) {
        localThis.sprintService.updateSprint(this.projectName, this.sprint, () => localThis.updateData());
      } else {
        const overlappingSprint = localThis.checkIfSprintsOverlap(startDate, endDate, sprints);
        this.createSprintErrorMessageRef = this.createSprintErrorDialog.open(CreateSprintErrorMessageComponent, {
          data: { name: overlappingSprint.name, startDate: overlappingSprint.startDate, endDate: overlappingSprint.endDate }
        });
      }
    });
  }

  private countSprintsInProject(projectName: string): Observable<Sprint[]> {
    return this.sprintService.getSprints(projectName);
  }

  checkIfSprintsOverlap(startDate: Date, endDate: Date, sprints: Sprint[]): Sprint {
    let overlappingSprint: Sprint = null;
    for (const sprint of sprints) {
      const tempStartDate = new Date(sprint.startDate);
      const tempEndDate = new Date(sprint.endDate);

      if (this.sprintsOverlap(startDate, endDate, tempStartDate, tempEndDate)) {
        overlappingSprint = sprint;
        return overlappingSprint;
      }
    }
    return overlappingSprint;
  }

  toggleSprintNameEdit(event: any): void {
    this.editModeSprintName = !this.editModeSprintName;
  }

  private sprintsOverlap(startDate1: Date, endDate1: Date, startDate2: Date, endDate2: Date): boolean {
    if ((startDate1.getTime() === startDate2.getTime() || endDate1.getTime() === endDate2.getTime()) ||
      (endDate1.getTime() > startDate2.getTime() && endDate1.getTime() < endDate2.getTime()) ||
      (startDate1.getTime() > startDate2.getTime() && startDate1.getTime() < endDate2.getTime())) {
      return true;
    }
    return false;
  }

  /**
   * Creates a new sprint but doesn't set it to active
   * ToDo: Correct start date and end date and check if sprints overlap (pipe,mergeMap)
   */
  createSprint(): void {
    const obj = this;
    const startDate = new Date(this.sprint.startDate);
    const endDate = this.addDaysToDate(startDate, this.currentSprintDurationAsDays);
    if (obj.projectName) {
      const data = obj.countSprintsInProject(obj.projectName);
      data.subscribe(() => {
        obj.sprintService.getSprints(this.projectName).subscribe(sprints => {
          const newSprint = new Sprint(null, this.sprint.name, startDate.toISOString(), endDate.toISOString(), null);

          if (!obj.checkIfSprintsOverlap(startDate, endDate, sprints)) {
            obj.sprintService.createSprint(this.projectName, this.sprint).subscribe(value => {
              newSprint.id = value.id;
            });
          } else {
            // TODO show dialog for overlapping sprints
            const overlappingSprint = obj.checkIfSprintsOverlap(startDate, endDate, sprints);
            this.createSprintErrorMessageRef = this.createSprintErrorDialog.open(CreateSprintErrorMessageComponent, {
              data: { name: overlappingSprint.name, startDate: overlappingSprint.startDate, endDate: overlappingSprint.endDate }
            });
          }
        });
      });
    }
  }

}
